export const M10_UNIT_2 = {
  id: "m10-u2-natural-world",
  title: "Unit 2: The Natural World",
  topic: "Setting the Scene & Nature Vocabulary",
  iconType: "leaf",
  grammarImage: "/m10-u2-natural-world.jpg",
  grammarText: "Unit focus: Using Past Continuous to set the scene and describe backgrounds + extensive nature vocabulary (celestial bodies, landscapes, animals, descriptive adjectives).",
  
  sets: [

    // ===================================================================
    // SET 1: CELESTIAL BODIES & SKY (B1+)
    // ===================================================================
    {
      id: "m10-u2-set1",
      title: "Set 1: The Night Sky",
      context: `*Monday evening - Nature Reserve campsite*

Emma arrives at the nature reserve at sunset.
The sun was setting behind the mountains.
Stars were beginning to appear in the dark sky.
She sets up her tent for the night.

*Lunes por la tarde - Campamento en reserva natural*

Emma llega a la reserva natural al atardecer.
El sol se ocultaba detrás de las montañas.
Las estrellas comenzaban a aparecer en el cielo oscuro.
Prepara su tienda para la noche.`,
      
      questions: [
        {
          type: "match",
          q: "Match the celestial bodies to their Spanish meaning:",
          pairs: [
            { left: "sun", right: "sol" },
            { left: "moon", right: "luna" },
            { left: "stars", right: "estrellas" }
          ],
          a: "completed",
          successTip: "Celestial bodies! Sun (THE sun, único), Moon (THE moon, único), Stars (plural, muchas). Articles matter! ☀️🌙⭐"
        },
        
        {
          type: "input",
          q: "Emma writes: 'The ___ was setting behind the mountains.' (sol)",
          a: "sun",
          alts: ["Sun"],
          tip: "Sun = sol (always with 'the')."
        },
        
        {
          type: "mc",
          q: "At night, Emma looked up. The ___ was shining brightly.",
          options: ["moon", "sun", "planet"],
          a: "moon",
          tip: "Moon = luna (night)."
        },
        
        {
          type: "input",
          q: "Emma's diary: 'The ___ were twinkling in the clear sky.' (estrellas)",
          a: "stars",
          alts: ["Stars"],
          tip: "Stars = estrellas (plural)."
        },
        
        {
          type: "match",
          q: "Match more celestial vocabulary:",
          pairs: [
            { left: "Earth", right: "Tierra (nuestro planeta)" },
            { left: "planet", right: "planeta" },
            { left: "sky", right: "cielo" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "Emma's notes: 'We live on planet ___ .'",
          options: ["Earth", "Moon", "Mars"],
          a: "Earth",
          tip: "Earth = Tierra (our planet).",
          successTip: "Earth vs earth! THE Earth (planeta) vs earth/soil (tierra/suelo). Capital letter = planet! 🌍"
        },
        
        {
          type: "input",
          q: "Emma observes: 'The ___ was completely dark and peaceful.' (cielo)",
          a: "sky",
          alts: ["Sky"],
          tip: "Sky = cielo."
        },
        
        {
          type: "order",
          q: "Traduce: 'Las estrellas brillaban en el cielo nocturno.'",
          words: ["The", "stars", "were", "shining", "in", "the", "night", "sky."],
          a: "The stars were shining in the night sky.",
          extra: ["sun", "moon", "shined"]
        },
        
        {
          type: "mc",
          q: "Emma's photo caption: 'A full ___ was rising over the lake.'",
          options: ["moon", "sun", "star"],
          a: "moon",
          tip: "Full moon = luna llena."
        },
        
        {
          type: "input",
          q: "Scene description: 'The ___ was glowing orange as it set.' (sol)",
          a: "sun",
          alts: ["Sun"],
          successTip: "Setting the scene! 'The sun was setting, the moon was rising, stars were appearing' = perfect background! 🎬"
        }
      ]
    },

    // ===================================================================
    // SET 2: WATER & LANDSCAPES (B1+)
    // ===================================================================
    {
      id: "m10-u2-set2",
      title: "Set 2: Water and Land",
      context: `*Tuesday morning - By the river*

Emma walks along the riverbank.
A small stream was flowing into the larger river.
In the distance, she could see the sea.
Gentle waves were washing onto the beach.

*Martes por la mañana - Junto al río*

Emma camina por la orilla del río.
Un pequeño arroyo fluía hacia el río más grande.
A la distancia, podía ver el mar.
Olas suaves llegaban a la playa.`,
      
      questions: [
        {
          type: "match",
          q: "Match the water bodies:",
          pairs: [
            { left: "stream", right: "arroyo (small)" },
            { left: "river", right: "río (larger)" },
            { left: "sea", right: "mar" }
          ],
          a: "completed",
          successTip: "Water hierarchy! Stream (arroyo) → River (río) → Sea (mar) → Ocean (océano). Size order! 💧"
        },
        
        {
          type: "input",
          q: "Emma's notes: 'A small ___ was flowing through the forest.' (arroyo)",
          a: "stream",
          alts: ["Stream"],
          tip: "Stream = arroyo (small water flow)."
        },
        
        {
          type: "mc",
          q: "Emma followed the ___ until it reached the ocean.",
          options: ["river", "stream", "wave"],
          a: "river",
          tip: "River = río (flows to sea)."
        },
        
        {
          type: "input",
          q: "Beach scene: 'The ___ were crashing loudly against the rocks.' (olas)",
          a: "waves",
          alts: ["Waves"],
          tip: "Waves = olas (in the sea)."
        },
        
        {
          type: "match",
          q: "Match more landscapes:",
          pairs: [
            { left: "beach", right: "playa" },
            { left: "desert", right: "desierto" },
            { left: "farmland", right: "tierras de cultivo" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "Emma walked on the sandy ___ collecting shells.",
          options: ["beach", "desert", "river"],
          a: "beach",
          tip: "Beach = playa (by the sea)."
        },
        
        {
          type: "input",
          q: "Landscape: 'The hot ___ stretched for miles with no water.'",
          a: "desert",
          alts: ["Desert"],
          tip: "Desert = desierto (dry, hot land).",
          successTip: "Desert vs Dessert! DESERT (desierto, 1 's') vs DESSERT (postre, 2 's'). Remember: you want 2 desserts! 🍰"
        },
        
        {
          type: "order",
          q: "Traduce: 'El río fluía tranquilamente hacia el mar.'",
          words: ["The", "river", "was", "flowing", "peacefully", "to", "the", "sea."],
          a: "The river was flowing peacefully to the sea.",
          extra: ["stream", "flowed", "ocean"]
        },
        
        {
          type: "mc",
          q: "Emma's photo: 'High ___ and low ___ affect the beach.'",
          options: ["tide / tide", "wave / wave", "sea / sea"],
          a: "tide / tide",
          tip: "Tide = marea (high/low).",
          successTip: "Tides! High tide (marea alta) = water comes in. Low tide (marea baja) = water goes out. Moon controls tides! 🌊"
        },
        
        {
          type: "input",
          q: "Rural scene: 'Cows were grazing peacefully on the ___ .'",
          a: "farmland",
          alts: ["Farmland","farms","fields"],
          successTip: "Farmland = agricultural land. Different from 'farm' (specific property). Farmland = general area! 🌾"
        }
      ]
    },

    // ===================================================================
    // SET 3: TREES & VEGETATION (B1+)
    // ===================================================================
    {
      id: "m10-u2-set3",
      title: "Set 3: Plants and Trees",
      context: `*Tuesday afternoon - Forest walk*

Emma enters a dense forest.
Tall trees were swaying gently in the breeze.
Their branches were moving and leaves were falling.
Grass and plants were growing everywhere.

*Martes por la tarde - Caminata en el bosque*

Emma entra a un bosque denso.
Árboles altos se mecían suavemente con la brisa.
Sus ramas se movían y hojas caían.
Pasto y plantas crecían por todas partes.`,
      
      questions: [
        {
          type: "match",
          q: "Match the tree parts:",
          pairs: [
            { left: "tree", right: "árbol" },
            { left: "branch", right: "rama" },
            { left: "leaf", right: "hoja" }
          ],
          a: "completed",
          successTip: "Tree anatomy! Tree (árbol) → Branch (rama) → Twig (ramita) → Leaf (hoja). From big to small! 🌳"
        },
        
        {
          type: "input",
          q: "Forest scene: 'Ancient ___ were standing tall in the forest.'",
          a: "trees",
          alts: ["Trees"],
          tip: "Trees = árboles (plural)."
        },
        
        {
          type: "mc",
          q: "Emma noticed the ___ were moving in the wind.",
          options: ["branches", "leafs", "grasses"],
          a: "branches",
          tip: "leafs is not correct! Leaves = hojas."
        },
        
        {
          type: "input",
          q: "Autumn scene: 'Colorful ___ were falling from the trees.'",
          a: "leaves",
          alts: ["Leaves"],
          tip: "Leaves = hojas (plural of leaf).",
          successTip: "Irregular plural! Leaf → Leaves (NOT leafs ❌). Like: wife→wives, knife→knives. 'F' becomes 'ves'! 🍂"
        },
        
        {
          type: "match",
          q: "Match vegetation types:",
          pairs: [
            { left: "grass", right: "pasto/hierba" },
            { left: "plant", right: "planta" },
            { left: "forest", right: "bosque" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "The green ___ was growing thick under the trees.",
          options: ["grass", "grasses", "leafs"],
          a: "grass",
          tip: "Grass = pasto (uncountable, no 's').",
          successTip: "Grass is uncountable! 'The grass' ✅ NOT 'grasses' ❌ (unless talking about types of grass - rare!). 🌱"
        },
        
        {
          type: "input",
          q: "Garden scene: 'Beautiful ___ were blooming everywhere.'",
          a: "plants",
          alts: ["Plants", "flowers", "bushes", "vegetation"],
          tip: "Plants = plantas (flowers, bushes, etc)."
        },
        
        {
          type: "order",
          q: "Traduce: 'El bosque estaba lleno de árboles y plantas.'",
          words: ["The", "forest", "was", "full", "of", "trees", "and", "plants."],
          a: "The forest was full of trees and plants.",
          extra: ["jungle", "leafs", "grass"]
        },
        
        {
          type: "mc",
          q: "Emma explored a tropical ___ with thick vegetation.",
          options: ["jungle", "forest", "desert"],
          a: "jungle",
          tip: "Jungle = selva (tropical, very dense).",
          successTip: "Forest vs Jungle! FOREST (bosque, temperate) vs JUNGLE (selva, tropical, denser). Same trees, different climate! 🌴"
        },
        
        {
          type: "input",
          q: "Small detail: 'A tiny ___ was hanging from the branch.'",
          a: "twig",
          alts: ["Twig"],
          successTip: "Twig = ramita (small branch). Very specific vocabulary for nature writing! Perfect for detailed descriptions! 🪵"
        }
      ]
    },

    // ===================================================================
    // SET 4: BIRDS IN NATURE (B2-)
    // ===================================================================
    {
      id: "m10-u2-set4",
      title: "Set 4: Birds of the Sky",
      context: `*Wednesday morning - Bird watching*

Emma sits quietly with her binoculars.
An eagle was soaring high above the mountains.
Seagulls were flying over the beach.
In the forest, an owl was resting in a tree.

*Miércoles por la mañana - Observación de aves*

Emma se sienta en silencio con sus binoculares.
Un águila volaba alto sobre las montañas.
Gaviotas volaban sobre la playa.
En el bosque, un búho descansaba en un árbol.`,
      
      questions: [
        {
          type: "match",
          q: "Match the birds:",
          pairs: [
            { left: "eagle", right: "águila (large, mountains)" },
            { left: "seagull", right: "gaviota (beach/sea)" },
            { left: "owl", right: "búho (nocturnal)" }
          ],
          a: "completed",
          successTip: "Birds by habitat! Eagle (montañas), Seagull (playa), Owl (bosques, noche). Each has its place! 🦅"
        },
        
        {
          type: "input",
          q: "Mountain scene: 'A majestic ___ was flying over the peaks.'",
          a: "eagle",
          alts: ["Eagle"],
          tip: "Eagle = águila (large bird of prey)."
        },
        
        {
          type: "mc",
          q: "At the beach, ___ were circling and calling loudly.",
          options: ["seagulls", "eagles", "owls"],
          a: "seagulls",
          tip: "Seagulls = gaviotas (at the beach)."
        },
        
        {
          type: "input",
          q: "Night scene: 'An ___ was hooting softly in the darkness.'",
          a: "owl",
          alts: ["Owl"],
          tip: "Owl = búho (nocturnal bird).",
          successTip: "Owl hoots! 'Hoot' = the sound owls make. 'The owl was hooting' = El búho ululaba. Onomatopoeia! 🦉"
        },
        
        {
          type: "match",
          q: "Match more birds:",
          pairs: [
            { left: "parrot", right: "loro (colorful, tropical)" },
            { left: "bird", right: "pájaro/ave (general)" },
            { left: "crow", right: "cuervo (black)" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "In the jungle, colorful ___ were making loud noises.",
          options: ["parrots", "owls", "eagles"],
          a: "parrots",
          tip: "Parrots = loros (tropical, noisy)."
        },
        
        {
          type: "input",
          q: "General term: 'Many different ___ were singing in the morning.'",
          a: "birds",
          alts: ["Birds", "Bird","pigeons","sparrows","parrots"],
          tip: "Birds = pájaros/aves (general term)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Las aves volaban hacia el sur.'",
          words: ["The", "birds", "were", "flying", "south."],
          a: "The birds were flying south.",
          extra: ["bird", "flew", "to"],
          successTip: "To the south is also possible! 'The birds were flying to the south' = Las aves estaban volando hacia el sur. Both correct! 🕊️"
        },
        
        {
          type: "mc",
          q: "Emma's photo: 'An ___ was spreading its magnificent wings.'",
          options: ["eagle", "seagull", "parrot"],
          a: "eagle",
          tip: "Eagles have magnificent large wings.",
          successTip: "Spread its wings = extender las alas. Perfect collocation! Birds spread wings, trees spread branches! 🪽"
        },
        
        {
          type: "input",
          q: "Dawn scene: 'All the ___ were starting to sing.'",
          a: "birds",
          alts: ["Birds"],
          successTip: "Dawn chorus! Birds sing most at dawn (sunrise). It's called the 'dawn chorus' in English! Nature fact! 🌅🎵"
        }
      ]
    },

    // ===================================================================
    // SET 5: INSECTS & SMALL CREATURES (B2-)
    // ===================================================================
    {
      id: "m10-u2-set5",
      title: "Set 5: Small Creatures",
      context: `*Wednesday afternoon - Garden exploration*

Emma explores a colorful garden.
Butterflies were dancing around the flowers.
Bees were buzzing and collecting pollen.
Small insects were crawling on the leaves.

*Miércoles por la tarde - Exploración del jardín*

Emma explora un jardín colorido.
Mariposas danzaban alrededor de las flores.
Abejas zumbaban y recolectaban polen.
Pequeños insectos se arrastraban sobre las hojas.`,
      
      questions: [
        {
          type: "match",
          q: "Match the insects:",
          pairs: [
            { left: "butterfly", right: "mariposa (colorful)" },
            { left: "bee", right: "abeja (makes honey)" },
            { left: "fly", right: "mosca (annoying)" }
          ],
          a: "completed",
          successTip: "Insects! Butterfly, Bee, Fly. Know the difference! 🦋🐝"
        },
        
        {
          type: "input",
          q: "Garden scene: 'Beautiful ___ were landing on the flowers.'",
          a: "butterflies",
          alts: ["Butterflies","bees","flies","insects","bugs","moths","mosquitos","mosquitoes"],
          tip: "Butterflies = mariposas (plural of butterfly)."
        },
        
        {
          type: "mc",
          q: "___ were flying from flower to flower collecting nectar.",
          options: ["Bees", "Flies", "Spiders"],
          a: "Bees",
          tip: "Bees = abejas (make honey)."
        },
        
        {
          type: "input",
          q: "Annoying moment: 'A ___ was buzzing around my head.'",
          a: "fly",
          alts: ["Fly","bee","mosquito","mosquitoes","mosquitos"],
          tip: "Fly = mosca (annoying insect).",
          successTip: "Fly = two meanings! FLY (verb, volar) and FLY (noun, mosca). Context tells you which! 🪰"
        },
        
        {
          type: "match",
          q: "Match more small creatures:",
          pairs: [
            { left: "mosquito", right: "mosquito (bites)" },
            { left: "spider", right: "araña (8 legs)" },
            { left: "insect", right: "insecto (general)" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "At night, ___ were biting everyone.",
          options: ["mosquitoes", "butterflies", "bees"],
          a: "mosquitoes",
          tip: "Mosquitoes = mosquitos (bite, annoying).",
          successTip: "Mosquito bites! 'A mosquito bit me' = me picó un mosquito. Bite (past: bit) = picar/morder! 🦟"
        },
        
        {
          type: "input",
          q: "Web discovery: 'A large ___ was building its web.'",
          a: "spider",
          alts: ["Spider"],
          tip: "Spider = araña (8 legs, makes webs)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Los insectos estaban volando alrededor de la luz.'",
          words: ["The", "insects", "were", "flying", "around", "the", "light."],
          a: "The insects were flying around the light.",
          extra: ["insect", "fly", "flies"]
        },
        
        {
          type: "mc",
          q: "Technical note: A ___ is NOT technically an insect (it has 8 legs).",
          options: ["spider", "bee", "butterfly"],
          a: "spider",
          tip: "Spiders = arachnids (8 legs, not insects).",
          successTip: "Science fact! Insects have 6 legs. Spiders have 8 legs = arachnids, not insects! 🕷️📚"
        },
        
        {
          type: "input",
          q: "Peaceful scene: 'A beautiful ___ was resting on a flower petal.'",
          a: "butterfly",
          alts: ["Butterfly","bee","ladybug"],
          successTip: "Butterfly life! Caterpillar (oruga) → Chrysalis (crisálida) → Butterfly (mariposa). Metamorphosis! 🐛🦋"
        }
      ]
    },

    // ===================================================================
    // SET 6: LAND ANIMALS (B2-)
    // ===================================================================
    {
      id: "m10-u2-set6",
      title: "Set 6: Wild Animals",
      context: `*Thursday morning - Safari adventure*

Emma's jeep drives through the African savanna.
Elephants were walking slowly to the waterhole.
Lions were resting under an acacia tree.
In the distance, giraffes were eating leaves.

*Jueves por la mañana - Aventura de safari*

El jeep de Emma conduce por la sabana africana.
Elefantes caminaban lentamente hacia el abrevadero.
Leones descansaban bajo un árbol de acacia.
A la distancia, jirafas comían hojas.`,
      
      questions: [
        {
          type: "match",
          q: "Match the African animals:",
          pairs: [
            { left: "elephant", right: "elefante (large, trunk)" },
            { left: "lion", right: "león (king of jungle)" },
            { left: "giraffe", right: "jirafa (long neck)" }
          ],
          a: "completed",
          successTip: "Big 5 safari animals! Lion, Elephant, Rhino, Buffalo, Leopard = most wanted to see on safari! 🦁🐘"
        },
        
        {
          type: "input",
          q: "Waterhole scene: 'Large ___ were drinking peacefully with their trunks.'",
          a: "elephants",
          alts: ["Elephants"],
          tip: "Elephants = elefantes (largest land animal).",
          successTip: "Elephants have trunks (trompa) for drinking and grabbing things. They also have tusks (colmillos) made of ivory! 🐘"
        },
        
        {
          type: "mc",
          q: "The pride of ___ was sleeping in the shade.",
          options: ["lions", "tigers", "bears"],
          a: "lions",
          tip: "Lions = leones (live in groups called 'prides').",
          successTip: "Collective nouns! A pride of lions, a herd of elephants, a tower of giraffes. Poetic English! 🦁"
        },
        
        {
          type: "input",
          q: "Tall sight: '___ were reaching for the highest leaves.'",
          a: "Giraffes",
          alts: ["giraffes"],
          tip: "Giraffes = jirafas (tallest animals).",
          successTip: "Collective nouns! A mob of kangaroos, a bloat of hippos, a crash of rhinos. Poetic English! 🦒"
        },
        
        {
          type: "match",
          q: "Match more land animals:",
          pairs: [
            { left: "tiger", right: "tigre (striped)" },
            { left: "bear", right: "oso (large, furry)" },
            { left: "monkey", right: "mono (lives in trees)" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "In India, wild ___ were walking through the jungle.",
          options: ["tigers", "lions", "giraffes"],
          a: "tigers",
          tip: "Tigers = tigres (striped, Asian).",
          successTip: "Lion vs Tiger! Lions (África, tawny) vs Tigers (Asia, striped). Different continents! 🐯"
        },
        
        {
          type: "input",
          q: "Forest sighting: 'A brown ___ was searching for food in the trees.'",
          a: "bear",
          alts: ["Bear"],
          tip: "Bear = oso (large mammal)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Los monos estaban saltando entre los árboles.'",
          words: ["The", "monkeys", "were", "jumping", "between", "the", "trees."],
          a: "The monkeys were jumping between the trees.",
          extra: ["monkey", "jump", "tree"]
        },
        
        {
          type: "mc",
          q: "Endangered species: A ___ was grazing (eating grass) with its baby.",
          options: ["rhino", "lion", "monkey"],
          a: "rhino",
          tip: "Rhino = rinoceronte (has horn).",
          successTip: "Rhino = short for rhinoceros! Like 'hippo' = hippopotamus. We shorten long animal names! 🦏"
        },
        
        {
          type: "input",
          q: "Australian wildlife: 'A ___ was hopping across the field.'",
          a: "kangaroo",
          alts: ["Kangaroo"],
          successTip: "Kangaroo hops! Specific verb for kangaroo movement. Not 'jump' - HOP! Each animal has its verb! 🦘"
        }
      ]
    },

    // ===================================================================
    // SET 7: REPTILES & MARINE LIFE (B2)
    // ===================================================================
    {
      id: "m10-u2-set7",
      title: "Set 7: Water and Reptile Life",
      context: `*Thursday afternoon - River and ocean exploration*

Emma explores the riverbank carefully.
A large crocodile was sunbathing on the mud.
Frogs were jumping between the water plants.
Later, at the ocean, dolphins were playing.

*Jueves por la tarde - Exploración de río y océano*

Emma explora la orilla del río con cuidado.
Un gran cocodrilo tomaba el sol en el lodo.
Ranas saltaban entre las plantas acuáticas.
Más tarde, en el océano, delfines jugaban.`,
      
      questions: [
        {
          type: "match",
          q: "Match the reptiles:",
          pairs: [
            { left: "crocodile", right: "cocodrilo (large, water)" },
            { left: "snake", right: "serpiente (no legs)" },
            { left: "lizard", right: "lagarto (small reptile)" }
          ],
          a: "completed",
          successTip: "Reptiles are cold-blooded! Crocodile, snake, lizard = all reptiles. Need sun to warm up! 🐊"
        },
        
        {
          type: "input",
          q: "Dangerous moment: 'A huge ___ was floating in the river.' (cocodrilo)",
          a: "crocodile",
          alts: ["Crocodile"],
          tip: "Crocodile = cocodrilo (dangerous reptile)."
        },
        
        {
          type: "mc",
          q: "Emma spotted a ___ slithering through the grass.",
          options: ["snake", "lizard", "frog"],
          a: "snake",
          tip: "Snake = serpiente (no legs, slithers).",
          successTip: "Slither = how snakes move! Specific verb. Snake slithers, lizard scurries, frog hops. Action verbs! 🐍"
        },
        
        {
          type: "input",
          q: "Pond life: '___ were croaking loudly at night.' (ranas)",
          a: "Frogs",
          alts: ["frogs"],
          tip: "Frogs = ranas (amphibians, croak)."
        },
        
        {
          type: "match",
          q: "Match marine animals:",
          pairs: [
            { left: "dolphin", right: "delfín (intelligent)" },
            { left: "whale", right: "ballena (largest)" },
            { left: "shark", right: "tiburón (predator)" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "In the ocean, ___ were jumping gracefully out of the water.",
          options: ["dolphins", "sharks", "fish"],
          a: "dolphins",
          tip: "Dolphins = delfines (intelligent mammals)."
        },
        
        {
          type: "input",
          q: "Magnificent sight: 'A huge ___ was spouting water from its blowhole.' (ballena)",
          a: "whale",
          alts: ["Whale"],
          tip: "Whale = ballena (largest animal).",
          successTip: "Blowhole = orificio nasal on top of whale's head! They breathe through it. Spout water = 'blow'! 🐋"
        },
        
        {
          type: "order",
          q: "Traduce: 'Los tiburones nadaban en aguas profundas.'",
          words: ["The", "sharks", "were", "swimming", "in", "deep", "waters."],
          a: "The sharks were swimming in deep waters.",
          extra: ["shark", "water", "swam"]
        },
        
        {
          type: "mc",
          q: "Colorful scene: 'Hundreds of ___ were swimming in a school.'",
          options: ["fish", "fishes", "shark"],
          a: "fish",
          tip: "Fish = pez/peces (same for singular and plural).",
          successTip: "Fish vs Fishes! FISH (plural, same species). FISHES (plural, different species). Usually 'fish'! 🐠"
        },
        
        {
          type: "input",
          q: "Beach discovery: 'A ___ was crawling sideways on the sand.' (cangrejo)",
          a: "crab",
          alts: ["Crab"],
          successTip: "Crabs walk sideways! Unique movement. 'The crab was scuttling sideways' = perfect description! 🦀"
        }
      ]
    },

    // ===================================================================
    // SET 8: DESCRIPTIVE ADJECTIVES - PEACEFUL SCENES (B2)
    // ===================================================================
    {
      id: "m10-u2-set8",
      title: "Set 8: Beautiful Descriptions",
      context: `*Friday morning - Writing time*

Emma writes about her favorite places.
"The village was so peaceful and quiet."
"The view from the mountain was magnificent."
"That little cottage was absolutely charming."

*Viernes por la mañana - Tiempo de escribir*

Emma escribe sobre sus lugares favoritos.
"El pueblo era tan tranquilo y silencioso."
"La vista desde la montaña era magnífica."
"Esa casita era absolutamente encantadora."`,
      
      questions: [
        {
          type: "match",
          q: "Match the descriptive adjectives:",
          pairs: [
            { left: "peaceful", right: "tranquilo, pacífico" },
            { left: "magnificent", right: "magnífico, impresionante" },
            { left: "charming", right: "encantador" }
          ],
          a: "completed",
          successTip: "Descriptive adjectives elevate your writing from simple to sophisticated! Literary language! ✍️"
        },
        
        {
          type: "input",
          q: "Emma's diary: 'The lake was so ___ , without a single sound.'",
          a: "peaceful",
          alts: ["Peaceful","calm","quiet","serene"],
          tip: "Peaceful = tranquilo, pacífico."
        },
        
        {
          type: "mc",
          q: "The ___ mountains were covered in snow and looked breathtaking.",
          options: ["magnificent", "peaceful", "charming"],
          a: "magnificent",
          tip: "Magnificent = magnífico, very impressive."
        },
        
        {
          type: "input",
          q: "Village description: 'The ___ little town had cobblestone streets.' (encantador)",
          a: "charming",
          alts: ["Charming"],
          tip: "Charming = encantador, delightful.",
          successTip: "Charming = typically for small, quaint places! 'Charming cottage, charming village.' Cobblestone = 'empedrado'! 🏘️"
        },
        
        {
          type: "match",
          q: "Match more descriptive words:",
          pairs: [
            { left: "picturesque", right: "pintoresco, como postal" },
            { left: "rural", right: "rural, del campo" },
            { left: "open", right: "abierto, espacioso" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "The ___ valley looked like a painting with its colorful houses.",
          options: ["picturesque", "open", "rural"],
          a: "picturesque",
          tip: "Picturesque = pintoresco, like a picture.",
          successTip: "'Picture' + 'esque' = like a picture! Similar: 'statuesque' (like a statue). Suffix '-esque'! 📸"
        },
        
        {
          type: "input",
          q: "Countryside: 'They lived in a ___ area far from the city.'",
          a: "rural",
          alts: ["Rural"],
          tip: "Rural = rural, countryside."
        },
        
        {
          type: "order",
          q: "Traduce: 'El paisaje abierto se extendía por millas.'",
          words: ["The", "open", "landscape", "stretched", "for", "miles."],
          a: "The open landscape stretched for miles.",
          extra: ["opened", "country", "mile"]
        },
        
        {
          type: "mc",
          q: "The ___ countryside had no buildings, just fields and sky.",
          options: ["open", "charming", "magnificent"],
          a: "open",
          tip: "Open country = campo abierto.",
          successTip: "Open country = no fences, no buildings. Wide, free space. Common in nature writing! 🌾"
        },
        
        {
          type: "input",
          q: "Paradise description: 'The beach was ___ with clear water and white sand.' (encantador)",
          a: "charming",
          alts: ["Charming", "beautiful", "lovely", "picturesque"],
          successTip: "Multiple adjectives work! But each has slightly different feeling. Charming = quaint. Beautiful = generally attractive! 🏖️"
        }
      ]
    },

    // ===================================================================
    // SET 9: DESCRIPTIVE ADJECTIVES - VIVID SCENES (B2+)
    // ===================================================================
    {
      id: "m10-u2-set9",
      title: "Set 9: Vivid Imagery",
      context: `*Friday afternoon - Poetic writing*

Emma writes her most descriptive passages.
"The colorful sunset painted the sky."
"The forest felt magical in the moonlight."
She wants readers to see what she saw.

*Viernes por la tarde - Escritura poética*

Emma escribe sus pasajes más descriptivos.
"El colorido atardecer pintaba el cielo."
"El bosque se sentía mágico a la luz de la luna."
Quiere que los lectores vean lo que ella vio.`,
      
      questions: [
        {
          type: "match",
          q: "Match vivid adjectives:",
          pairs: [
            { left: "colorful", right: "colorido, lleno de colores" },
            { left: "magical", right: "mágico, maravilloso" },
            { left: "breathtaking", right: "impresionante, quita aliento" }
          ],
          a: "completed",
          successTip: "Sensory adjectives! Appeal to the senses = powerful writing. Make readers FEEL the scene! 🎨"
        },
        
        {
          type: "input",
          q: "Sunset: 'The ___ sky was filled with reds, oranges, and purples.'",
          a: "colorful",
          alts: ["Colorful", "full of colors", "vibrant", "bright", "vivid", "colourful"],
          tip: "Colorful = colorido, full of colors."
        },
        
        {
          type: "mc",
          q: "The forest at dawn felt ___ , like something from a fairy tale.",
          options: ["magical", "colorful", "rural"],
          a: "magical",
          tip: "Magical = mágico, mysterious and wonderful."
        },
        
        {
          type: "input",
          q: "Impressive view: 'The ___ view left everyone speechless.'",
          a: "breathtaking",
          alts: ["Breathtaking", "magnificent"],
          tip: "Breathtaking = quita el aliento, amazing.",
          successTip: "'Breathtaking' literally means it takes your breath away! 'Breath' + 'taking'. So beautiful you can't breathe! 😮"
        },
        
        {
          type: "match",
          q: "Match intensity levels:",
          pairs: [
            { left: "nice", right: "pleasant (mild)" },
            { left: "beautiful", right: "very attractive (strong)" },
            { left: "breathtaking", right: "stunning (extreme)" }
          ],
          a: "completed",
          successTip: "Gradation! Nice → Beautiful → Magnificent → Breathtaking. Increase intensity! Learn scales! 📈"
        },
        
        {
          type: "mc",
          q: "The scene was so ___ that she had to stop and stare.",
          options: ["stunning", "peaceful", "rural"],
          a: "stunning",
          tip: "Stunning = impactante, extremely beautiful.",
          successTip: "Stunning = from 'stun' (aturdir). So beautiful it stuns you! Another intensity word! 💫"
        },
        
        {
          type: "input",
          q: "Vivid scene: 'The ___ flowers created a carpet of colors.' (vibrantes)",
          a: "vibrant",
          alts: ["Vibrant", "colorful"],
          tip: "Vibrant = vibrante, bright and strong colors."
        },
        
        {
          type: "order",
          q: "Traduce: 'El bosque mágico brillaba bajo la luna.'",
          words: ["The", "magical", "forest", "was", "glowing", "under", "the", "moon."],
          a: "The magical forest was glowing under the moon.",
          extra: ["magic", "shine", "sun"]
        },
        
        {
          type: "mc",
          q: "The ___ landscape seemed to go on forever without end.",
          options: ["endless", "colorful", "charming"],
          a: "endless",
          tip: "Endless = interminable, infinite."
        },
        
        {
          type: "input",
          q: "Perfect description: 'It was an ___ moment of pure beauty.' (inolvidable)",
          a: "unforgettable",
          alts: ["Unforgettable", "memorable"],
          successTip: "Unforgettable = 'un-' (not) + 'forget' + '-able' (can be). Cannot be forgotten! Prefix + root + suffix! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 10: SETTING THE SCENE - COMPLETE NARRATIVES (B2+)
    // ===================================================================
    {
      id: "m10-u2-set10",
      title: "Set 10: Painting with Words",
      context: `*Saturday - Final article*

Emma writes her nature article.
"The sun was setting behind the mountains while eagles were soaring overhead and the river was flowing peacefully below."
She connects all her observations beautifully.

*Sábado - Artículo final*

Emma escribe su artículo de naturaleza.
"El sol se ocultaba tras las montañas mientras águilas volaban en lo alto y el río fluía pacíficamente abajo."
Conecta todas sus observaciones bellamente.`,
      
      questions: [
        {
          type: "match",
          q: "Match scene-setting techniques:",
          pairs: [
            { left: "Multiple actions", right: "Birds were singing and flying" },
            { left: "Simultaneous", right: "While sun was setting, birds were flying" },
            { left: "Background details", right: "The river was flowing peacefully" }
          ],
          a: "completed",
          successTip: "Scene-setting = multiple Past Continuous actions! Creates atmosphere before main events! 🎬"
        },
        
        {
          type: "input",
          q: "Opening: 'The sun was ___ and the sky was turning orange.'",
          a: "setting",
          alts: ["Setting"],
          tip: "Set (sun) = ponerse (el sol)."
        },
        
        {
          type: "mc",
          q: "Scene: 'Eagles were ___ high above while deer were ___ below.'",
          options: ["soaring / grazing", "soaring / grazed", "soared / grazing"],
          a: "soaring / grazing",
          tip: "Both actions simultaneous = both Past Continuous.",
          successTip: "'Soar' = volar alto sin esfuerzo. 'Graze' = pastar. Specific verbs paint better pictures! 🦅🦌"
        },
        
        {
          type: "order",
          q: "Traduce: 'Las aves cantaban mientras el sol salía.'",
          words: ["The", "birds", "were", "singing", "while", "the", "sun", "was", "rising."],
          a: "The birds were singing while the sun was rising.",
          extra: ["sang", "rise", "song"]
        },
        
        {

  type: "order",
  q: "Traduce: 'Los árboles se mecían y las hojas caían.'",
  words: ["The", "trees", "were", "swaying", "and", "leaves", "were", "falling."],
  a: "The trees were swaying and leaves were falling.",
  extra: ["swayed", "fell", "are"],
  successTip: "¡Muy bien! Usaste 'were' dos veces para coordinar dos acciones en progreso. 🎨"
        },
        
        {
          type: "mc",
          q: "Vivid opening: 'The ___ sun was painting the sky with gold and pink.'",
          options: ["setting", "set", "sets"],
          a: "setting",
          tip: "Past Continuous for background scene."
        },
        
        {
          type: "order",
          q: "Traduce: 'Los delfines saltaban mientras el barco navegaba.'",
          words: ["The", "dolphins", "were", "jumping", "while", "the", "boat", "was", "sailing."],
          a: "The dolphins were jumping while the boat was sailing.",
          extra: ["jumped", "sail", "ship"]
        },
        
        {
          type: "input",
          q: "Atmosphere: 'A gentle breeze was ___ through the trees.' (soplar)",
          a: "blowing",
          alts: ["Blowing"],
          tip: "Blow = soplar (wind).",
          successTip: "'A breeze was blowing' = common collocation. Wind blows, sun shines, rain falls. Learn verb + noun pairs! 🍃"
        },
        
        {
          type: "mc",
          q: "Complete scene: 'While the stars ___ appearing, owls ___ hooting.'",
          options: ["were / were", "were / was", "was / were"],
          a: "were / were",
          tip: "Stars (plural) = were. Owls (plural) = were."
        },
        
        {
          type: "order",
          q: "Traduce: 'El río fluía mientras pájaros cantaban en los árboles.'",
          words: ["The", "river", "was", "flowing", "while", "birds", "were", "singing", "in", "the", "trees."],
          a: "The river was flowing while birds were singing in the trees.",
          extra: ["sang", "flow", "tree"],
          successTip: "Perfect scene-setting! You can now create vivid backgrounds for stories. Literary skill unlocked! 📖✨"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u2-reading",
    title: "Reading: Emma's Nature Journal",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**Emma's Nature Reserve Journal - Day 1**

Monday, 6:00 PM

I arrived at the Woodland Nature Reserve just as the sun was setting. The sky was turning beautiful shades of orange and purple. As I walked to my campsite, small birds were singing their evening songs in the trees.

By the riverbank, I saw something magnificent - a heron was standing perfectly still in the shallow water, waiting for fish. The peaceful river was flowing gently, and the only sounds were the wind rustling through the leaves and insects buzzing in the warm evening air.

I set up my tent while the stars were beginning to appear above. It felt magical being surrounded by nature. Tomorrow I plan to explore the forest trails and hopefully spot some of the larger animals that live here.`
      },
      
      {
        type: "mc",
        q: "What time did Emma arrive?",
        options: ["At sunset (6:00 PM)", "In the morning", "At midnight"],
        a: "At sunset (6:00 PM)",
        tip: "Check the opening sentence."
      },
      
      {
        type: "mc",
        q: "What was the sky doing?",
        options: ["Turning orange and purple", "Completely dark", "Bright blue"],
        a: "Turning orange and purple",
        tip: "'The sky was turning...'"
      },
      
      {
        type: "mc",
        q: "What was the heron doing?",
        options: ["Standing still in the water", "Flying overhead", "Building a nest"],
        a: "Standing still in the water",
        tip: "'A heron was standing perfectly still...'",
        successTip: "Heron = garza! Large wading bird. 'Wade' = caminar en agua poco profunda. Common at rivers! 🦩"
      },
      
      {
        type: "mc",
        q: "How did Emma describe the scene?",
        options: ["Peaceful and magical", "Loud and exciting", "Cold and dark"],
        a: "Peaceful and magical",
        tip: "Look for descriptive adjectives she used."
      },
      
      {
        type: "mc",
        q: "What was Emma doing while the stars were appearing?",
        options: ["Setting up her tent", "Watching the heron", "Walking by the river"],
        a: "Setting up her tent",
        tip: "'I set up my tent while the stars were beginning...'"
      },
      
      {
        type: "passage",
        passage: `**Emma's Nature Reserve Journal - Day 3**

Wednesday, 8:30 AM

This morning was incredible! I woke up early and walked to the observation point on the hill. The sun was just rising over the mountains, painting the whole valley in golden light.

Below me, a family of deer were grazing peacefully in an open meadow. The mother was keeping watch while the young ones were eating. Further away, I could see the river winding through the landscape like a silver ribbon.

Birds were everywhere - eagles were soaring high above the peaks, and smaller songbirds were hopping between the branches of nearby trees. A colorful butterfly landed on my hand and stayed there for a few moments before flying away.

The whole scene felt like a living painting. This is why I love being a nature photographer - capturing these magical moments that most people never get to see.`
      },
      
      {
        type: "mc",
        q: "What time did Emma wake up?",
        options: ["Early morning (8:30 AM)", "At noon", "Late afternoon"],
        a: "Early morning (8:30 AM)",
        tip: "Check the timestamp."
      },
      
      {
        type: "mc",
        q: "What were the deer doing?",
        options: ["Grazing in a meadow", "Drinking from the river", "Running away"],
        a: "Grazing in a meadow",
        tip: "'A family of deer were grazing...'"
      },
      
      {
        type: "mc",
        q: "Where were the eagles?",
        options: ["Soaring above the peaks", "In the trees", "On the ground"],
        a: "Soaring above the peaks",
        tip: "'Eagles were soaring high above...'"
      },
      
      {
        type: "mc",
        q: "What happened with the butterfly?",
        options: ["It landed on her hand briefly", "It flew away immediately", "It stayed on her shoulder"],
        a: "It landed on her hand briefly",
        tip: "'...landed on my hand...for a few moments'",
        successTip: "Landed ON (not IN). ON your hand, ON your shoulder. Prepositions matter! 🦋"
      },
      
      {
        type: "mc",
        q: "How did Emma describe the whole scene?",
        options: ["Like a living painting and magical", "Scary and dangerous", "Boring and quiet"],
        a: "Like a living painting and magical",
        tip: "Look for her descriptive words.",
        successTip: "Perfect nature writing! Setting scenes with Past Continuous + vivid adjectives = professional! 📸✨"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u2-review",
    title: "Unit 2 Review: The Natural World",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: At night, the ___ and ___ were shining in the sky.",
        options: ["moon / stars", "sun / moon", "stars / sun"],
        a: "moon / stars",
        tip: "Moon and stars = nighttime."
      },
      
      {
        type: "mc",
        q: "Review: The ___ was flowing peacefully to the sea.",
        options: ["river", "stream", "wave"],
        a: "river",
        tip: "River flows to the sea."
      },
      
      {
        type: "mc",
        q: "Review: An ___ was soaring high above the mountains.",
        options: ["eagle", "owl", "seagull"],
        a: "eagle",
        tip: "Eagle = mountain bird of prey."
      },
      
      {
        type: "mc",
        q: "Review: Colorful ___ were dancing around the flowers.",
        options: ["butterflies", "bees", "flies"],
        a: "butterflies",
        tip: "Butterflies = mariposas (dance around flowers)."
      },
      
      {
        type: "mc",
        q: "Review: A family of ___ were drinking at the waterhole.",
        options: ["elephants", "lions", "tigers"],
        a: "elephants",
        tip: "Elephants drink at waterholes."
      },
      
      {
        type: "mc",
        q: "Review: ___ were jumping and playing in the ocean.",
        options: ["Dolphins", "Sharks", "Whales"],
        a: "Dolphins",
        tip: "Dolphins = playful marine mammals."
      },
      
      {
        type: "mc",
        q: "Review: The ___ village looked like a postcard.",
        options: ["picturesque", "rural", "open"],
        a: "picturesque",
        tip: "Picturesque = like a picture/postcard."
      },
      
      {
        type: "mc",
        q: "Review: The forest felt ___ in the moonlight.",
        options: ["magical", "rural", "open"],
        a: "magical",
        tip: "Magical = mágico, mysterious."
      },
      
      {
        type: "mc",
        q: "Review: The sun was ___ while birds were ___ .",
        options: ["setting / singing", "set / singing", "setting / sang"],
        a: "setting / singing",
        tip: "Both Past Continuous for simultaneous actions."
      },
      
      {
        type: "mc",
        q: "Review: The view was absolutely ___ .",
        options: ["breathtaking", "peaceful", "rural"],
        a: "breathtaking",
        tip: "Breathtaking = extremely beautiful.",
        successTip: "🎉 UNIT 2 COMPLETE! Nature vocabulary mastered! Setting scenes like a pro! Ready for Unit 3! 🌿✨"
      }
    ]
  }
};
